import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  useEffect(() => {
    if (slug && !categories.find((c) => c.slug === slug)) {
      navigate('/category/chise-jingling', { replace: true });
    }
  }, [slug, navigate]);

  if (!currentCategory) {
    return null;
  }

  return (
    <>
      <ScrollToTop />
      <Navbar transparent={false} />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '冒险篇章', to: '/category/chise-jingling' },
                { label: currentCategory.name },
              ]}
            />

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100">
                  <i className={`${currentCategory.icon} text-lg text-accent-600`}></i>
                </span>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
              </div>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {currentCategory.description}
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="w-full lg:w-56 flex-shrink-0">
                <nav aria-label="精灵分类" className="sticky top-20">
                  <h3 className="font-heading text-sm font-semibold text-foreground-700 mb-3">
                    精灵分类
                  </h3>
                  <ul className="space-y-1">
                    {categories.map((cat) => (
                      <li key={cat.slug}>
                        <Link
                          to={`/category/${cat.slug}`}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                            slug === cat.slug
                              ? 'bg-primary-100 text-primary-700 font-medium'
                              : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                          }`}
                        >
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i className={`${cat.icon} text-xs`}></i>
                          </span>
                          {cat.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              </aside>

              <div className="flex-1 min-w-0">
                {categoryArticles.length > 0 ? (
                  <div className="space-y-5">
                    {categoryArticles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className="group flex flex-col sm:flex-row bg-background-100 rounded-lg overflow-hidden hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
                      >
                        <div className="relative w-full sm:w-56 h-44 sm:h-40 flex-shrink-0 overflow-hidden">
                          <img
                            src={article.imageUrl}
                            alt={`好色精灵探访记 - ${article.title}`}
                            title={`好色精灵探访记 ${article.title}`}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 p-5 flex flex-col justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className="text-xs text-foreground-400">{article.readTime}</span>
                              {article.tags.slice(0, 2).map((tag) => (
                                <span key={tag} className="inline-block px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                                  {tag}
                                </span>
                              ))}
                            </div>
                            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                              {article.title}
                            </h3>
                            <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                              {article.summary}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 mt-3 text-xs text-foreground-400">
                            <span>{article.publishDate}</span>
                            <span>·</span>
                            <span>{article.author}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <span className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                      <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                    </span>
                    <p className="text-foreground-500">该分类下暂无文章</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}